import { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";

export const EMPTY_NOTIFY_CHANNELS = {
  email: false,
  sms: false,
  whatsapp: false,
};

// Drop legacy session cache that could keep stale "all off" after enabling in admin.
try {
  sessionStorage.removeItem("siteNotifyChannels");
} catch {
  // ignore
}

function asEnabled(value) {
  return value === true || value === "true" || value === 1;
}

function normalizeChannels(data) {
  return {
    email: asEnabled(data?.notify_email_enabled),
    sms: asEnabled(data?.notify_sms_enabled),
    whatsapp: asEnabled(data?.notify_whatsapp_enabled),
  };
}

export function anyNotifyChannelEnabled(channels) {
  return Boolean(channels?.email || channels?.sms || channels?.whatsapp);
}

/**
 * Which notification channels the site currently offers (from site_settings).
 */
export async function fetchNotifyChannels() {
  try {
    const { data, error } = await supabase
      .from("site_settings")
      .select(
        "notify_email_enabled, notify_sms_enabled, notify_whatsapp_enabled"
      )
      .eq("id", 1)
      .maybeSingle();

    if (error) throw error;
    return normalizeChannels(data);
  } catch (err) {
    console.error("Failed to load notify channels:", err);
    return { ...EMPTY_NOTIFY_CHANNELS };
  }
}

export function useNotifyChannels() {
  const [channels, setChannels] = useState(EMPTY_NOTIFY_CHANNELS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchNotifyChannels()
      .then((next) => {
        if (!cancelled) setChannels(next);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { channels, loading };
}

/** Build profile patch from opt-in UI state, respecting site flags. */
export function notifyPrefsToProfilePatch(prefs, channels) {
  const email = channels?.email === true && prefs?.email === true;
  const phone = prefs?.phoneChannel;
  const sms =
    channels?.sms === true && phone === "sms";
  const whatsapp =
    channels?.whatsapp === true && phone === "whatsapp";
  return {
    notify_email: email,
    notify_sms: sms,
    notify_whatsapp: whatsapp,
  };
}

export function notifyPrefsFromProfile(profile, channels) {
  const email = channels?.email === true && profile?.notify_email === true;
  let phoneChannel = "";
  if (channels?.sms === true && profile?.notify_sms === true) {
    phoneChannel = "sms";
  } else if (
    channels?.whatsapp === true &&
    profile?.notify_whatsapp === true
  ) {
    phoneChannel = "whatsapp";
  }
  return { email, phoneChannel };
}
